import React from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, Breadcrumbs, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink } from 'react-router-dom';

export interface Breadcrumb {
  label: string;
  href?: string;
}

export interface ModuleHeaderProps {
  title: string;
  breadcrumbs?: Breadcrumb[];
  moduleColor: string;
  actions?: React.ReactNode;
  subtitle?: string;
}

/**
 * ModuleHeader - Page header with animated underline and breadcrumbs
 *
 * Features:
 * - Breadcrumb navigation with hover animation
 * - Module color underline (4px, animated width on mount)
 * - Title with fade-in animation
 * - Optional action button area (right side)
 * - Inter font for headings
 *
 * Usage:
 * <ModuleHeader
 *   title="Email Dashboard"
 *   breadcrumbs={[
 *     { label: 'Home', href: '/' },
 *     { label: 'Email' },
 *   ]}
 *   moduleColor={theme.palette.email.main}
 *   actions={<Button>Send Email</Button>}
 * />
 */
export const ModuleHeader: React.FC<ModuleHeaderProps> = ({
  title,
  breadcrumbs,
  moduleColor,
  actions,
  subtitle,
}) => {
  const theme = useTheme();

  const underlineVariants = {
    hidden: { width: 0 },
    visible: {
      width: '100%',
      transition: {
        duration: 0.6,
        ease: [0.25, 0.8, 0.25, 1] as const,
        delay: 0.2,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut' as const,
      },
    },
  };

  const breadcrumbVariants = {
    hidden: { opacity: 0, y: -5 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut' as const,
      },
    },
  };

  return (
    <Box
      sx={{
        marginBottom: theme.spacing(4),
        position: 'relative',
      }}
    >
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <motion.div
          variants={breadcrumbVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: theme.spacing(1.5) }}
        >
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                color: theme.palette.text.secondary,
                opacity: 0.5,
              },
            }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;

              return crumb.href && !isLast ? (
                <Link
                  key={index}
                  component={RouterLink}
                  to={crumb.href}
                  underline="hover"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.875rem',
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    transition: `color ${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeInOut}`,
                    '&:hover': {
                      color: moduleColor,
                    },
                  }}
                >
                  {crumb.label}
                </Link>
              ) : (
                <Typography
                  key={index}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: '0.875rem',
                    color: isLast ? theme.palette.text.primary : theme.palette.text.secondary,
                    fontWeight: isLast ? 600 : 500,
                  }}
                >
                  {crumb.label}
                </Typography>
              );
            })}
          </Breadcrumbs>
        </motion.div>
      )}

      {/* Header container with title and actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: theme.spacing(1),
        }}
      >
        {/* Title and subtitle */}
        <Box sx={{ flex: 1 }}>
          <motion.div variants={titleVariants} initial="hidden" animate="visible">
            <Typography
              variant="h1"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                color: theme.palette.text.primary,
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                marginBottom: subtitle ? theme.spacing(0.5) : 0,
              }}
            >
              {title}
            </Typography>
          </motion.div>

          {subtitle && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  color: theme.palette.text.secondary,
                  fontSize: '1.125rem',
                  marginTop: theme.spacing(0.5),
                }}
              >
                {subtitle}
              </Typography>
            </motion.div>
          )}
        </Box>

        {/* Actions */}
        {actions && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            style={{
              marginLeft: theme.spacing(3),
              display: 'flex',
              gap: theme.spacing(1),
              alignItems: 'center',
            }}
          >
            {actions}
          </motion.div>
        )}
      </Box>

      {/* Animated underline */}
      <Box
        sx={{
          position: 'relative',
          height: 4,
          overflow: 'hidden',
        }}
      >
        <motion.div
          variants={underlineVariants}
          initial="hidden"
          animate="visible"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            backgroundColor: moduleColor,
            borderRadius: theme.shape.borderRadius / 2,
          }}
        />
      </Box>
    </Box>
  );
};
